import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
  upsertOrGroup,
} from "@/shared/contracts/query-helpers";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

type DepartmentItem = { id: string; name: string };
type AuditTypeItem = { id: string; name: string };

type AuditPlanFilterProps = {
  routePath?: "/app/internal-audit" | "/app/internal-audit/records/new";
  preservePlanId?: boolean;
};

export function AuditPlanFilter({
  routePath = "/app/internal-audit",
  preservePlanId = false,
}: AuditPlanFilterProps = {}) {
  const nav = useNavigate({ from: routePath });
  const search = useSearch({ from: routePath }) as OffsetPageQueryDTO & {
    planId?: string;
  };
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const nameFilter = findCondition(filters, "name");
  const statusFilter = findCondition(filters, "status");
  const departmentFilter = findCondition(filters, "departmentId");
  const auditTypeFilter = findCondition(filters, "auditTypeId");
  const frequencyFilter = findCondition(filters, "frequency");

  const [searchValue, setSearchValue] = useState<string>(
    (nameFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((nameFilter?.value as string) || ""),
    [nameFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertOrGroup(filters, [
        { field: "name", op: "contains", value: val },
        { field: "auditTypeName", op: "contains", value: val },
      ]);
    } else {
      nextFilters = removeConditions(filters, "name");
      nextFilters = removeConditions(nextFilters, "auditTypeName");
    }
    if (routePath === "/app/internal-audit/records/new") {
      nav({
        search: {
          offset: search.offset ?? 0,
          limit: search.limit ?? 20,
          sort: search.sort,
          filters: nextFilters?.length ? nextFilters : undefined,
          planId: preservePlanId && search.planId ? search.planId : undefined,
        },
      });
    } else {
      nav({
        search: {
          offset: search.offset ?? 0,
          limit: search.limit ?? 20,
          sort: search.sort,
          filters: nextFilters?.length ? nextFilters : undefined,
        },
      });
    }
  }, 400);

  const statusValue =
    typeof statusFilter?.value === "string" ? String(statusFilter.value) : "";

  const departmentValue = (departmentFilter?.value as string) || "";
  const auditTypeValue = (auditTypeFilter?.value as string) || "";
  const frequencyValue =
    typeof frequencyFilter?.value === "string"
      ? String(frequencyFilter.value)
      : "";

  const hasFilter =
    !!nameFilter?.value ||
    !!statusValue ||
    !!departmentValue ||
    !!auditTypeValue ||
    !!frequencyValue;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2" data-tourid="plan-filter">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາຜະແນການກວດສອບ..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <InfiniteCombobox<DepartmentItem>
            queryKey={["departments", "filter-audit-plan"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/internal-audit/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/internal-audit/departments/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={departmentValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "departmentId",
                  op: "eq",
                  value: val,
                });
              } else {
                nextFilters = removeConditions(filters, "departmentId");
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: search.sort,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ເລືອກພະແນກ"
            className="h-8 w-full sm:w-40"
            clearable
          />
          <InfiniteCombobox<AuditTypeItem>
            queryKey={["audit-types", "filter-audit-plan"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/internal-audit/audit-types/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/internal-audit/audit-types/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={auditTypeValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "auditTypeId",
                  op: "eq",
                  value: val,
                });
              } else {
                nextFilters = removeConditions(filters, "auditTypeId");
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: search.sort,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ເລືອກປະເພດ"
            className="h-8 w-full sm:w-40"
            clearable
          />
          <SimpleSelect
            value={frequencyValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "frequency");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "frequency",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: search.sort,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ຄວາມຖີ່"
            className="h-8 w-full sm:w-32"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "monthly", label: "ປະຈຳເດືອນ" },
              { value: "quarterly", label: "ປະຈຳໄຕມາດ" },
              { value: "semi_annual", label: "ຄັ້ງຕໍ່ 6 ເດືອນ" },
              { value: "annual", label: "ປະຈຳປີ" },
              { value: "ad_hoc", label: "ຕາມຄວາມຈຳເປັນ" },
            ]}
          />
          <SimpleSelect
            value={statusValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: search.sort,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-32"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "pending", label: "ຍັງບໍ່ກວດສອບ" },
              { value: "in_progress", label: "ກຳລັງກວດສອບ" },
              { value: "completed", label: "ກວດສອບແລ້ວ" },
              { value: "cancelled", label: "ຍົກເລີກ" },
            ]}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({
                search: {
                  ...search,
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: search.sort,
                  filters: undefined,
                },
              })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
